import { FC, useEffect, useMemo, useState } from 'react'
import SecondaryButton from '@pages/Admin/CourseSettings/components/SecondaryButton/SecondaryButton'
import cn from './index.module.css'
import CourseInfoCard from './components/CourseInfoCard/CourseInfoCard'
import SelectCourseCard, {
  ICourseEnrollmentItem,
} from './components/SelectCourseCard/SelectCourseCard'
import { fetchCoursesWithTariffs } from '../../utils/api'
import { useGetSettings } from '@pages/CoursesCatalog/tabs/CatalogCoursesTab/hooks/getPlatformSettings'
import { getDiscount } from '../../utils/tariff'
import { getTariffValueMin } from '../SelectTariffValue/SelectTariffValue'

interface ISelectAdditionalCourses {
  userCourses?: number[]
  isFirstFree: boolean
  currentCourseId: number
  addedCourses: ICourseEnrollmentItem[]
  canManage: boolean
  setAddedCourses: React.Dispatch<React.SetStateAction<ICourseEnrollmentItem[]>>
}

const SelectAdditionalCourses: FC<ISelectAdditionalCourses> = ({
  userCourses = [],
  isFirstFree,
  currentCourseId,
  addedCourses,
  canManage = true,
  setAddedCourses,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [courses, setCourses] = useState<ICourse[]>([])
  const { getSettings } = useGetSettings()
  const discountsUnit = getSettings('extraCoursesDiscountUnit')
  const extraCoursesDiscounts = JSON.parse(
    getSettings('extraCoursesDiscounts') ?? 'null',
  )

  useEffect(() => {
    setAddedCourses((prev) =>
      prev?.filter((course) => !userCourses?.includes(course?.course?.id!)),
    )
    setCourses((prev) =>
      prev?.filter((course) => !userCourses?.includes(course?.id!)),
    )
  }, [userCourses?.length])

  const fetchCourses = async () => {
    setIsLoading(true)
    const res = await fetchCoursesWithTariffs()
    setCourses(
      res
        .filter((course) => course?.id !== currentCourseId && !!course?.view)
        .filter((course) => !userCourses?.includes(course?.id!))
        .map((course) => {
          const filteredTariffs =
            course.directionTariffs?.filter((tariff) => !!tariff?.view) || []
          return {
            ...course,
            directionTariffs: filteredTariffs,
          }
        })
        .filter((course) => course.directionTariffs.length > 0) ?? [],
    )
    setIsLoading(false)
  }

  const onClickAdd = () => {
    if (!courses?.length) {
      fetchCourses()
    }
    setAddedCourses((prev) => [
      ...prev,
      {
        course: null,
        tariffId: null,
        value: null,
        withDemo: false,
        enrollDate: null,
      },
    ])
  }

  const getAdditionalCoursesFromParams = async () => {
    let parsedCourses: ICourseEnrollmentItem[] = []
    try {
      const params = new URLSearchParams(window.location.search)
      const rawCourses = params.get('additional_courses')

      if (rawCourses) {
        const parsed: any[] = JSON.parse(rawCourses)

        if (!parsed?.length) {
          return
        }
        if (!courses?.length) {
          await fetchCourses()
        }

        for (const item of parsed) {
          let courseItem: ICourseEnrollmentItem = {
            course: null,
            tariffId: null,
            value: null,
            withDemo: false,
            enrollDate: null,
            isLocked: false,
          }

          const course = courses?.find(
            (course) => course?.id === item?.courseId,
          )
          if (!course) continue
          courseItem.course = course

          const tariff =
            course?.directionTariffs?.find(
              (tariff) => tariff?.id === item?.tariffId,
            ) ?? null
          courseItem.tariffId = tariff?.id ?? null

          courseItem.value = tariff
            ? item?.value
              ? item?.value
              : getTariffValueMin(tariff)
            : 1

          if (tariff && item?.value) {
            courseItem.isLocked = true
          }

          courseItem.withDemo = item?.withDemo ?? false
          courseItem.enrollDate = item?.enrollDate ?? null

          parsedCourses.push(courseItem)
        }
      }
    } catch (error) {
      console.error('Ошибка при парсинге параметров курса', error)
    }
    if (parsedCourses?.length) {
      setAddedCourses(parsedCourses)
    }
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const rawCourses = params.get('additional_courses')

    if (!!rawCourses && !courses?.length) {
      fetchCourses()
    }
    if (!!rawCourses && courses?.length) {
      getAdditionalCoursesFromParams()
    }
  }, [courses?.length])

  const onRemove = (index: number) => {
    setAddedCourses((prev) => prev?.filter((_, i) => i !== index))
  }

  const onChange = (
    index: number,
    key: keyof ICourseEnrollmentItem,
    value: number | string | boolean | null | object,
  ) => {
    setAddedCourses(
      (prev) =>
        prev.map((item, i) =>
          i === index ? { ...item, [key]: value } : item,
        ) ?? [],
    )
  }

  const lastCourse = addedCourses?.[addedCourses.length - 1]

  const canAddCourse =
    canManage &&
    addedCourses?.length < 5 &&
    lastCourse?.course !== null &&
    lastCourse?.tariffId !== null &&
    lastCourse?.value !== null

  const getAddButtonString = () => {
    const discount = getDiscount(
      isFirstFree,
      addedCourses?.length,
      addedCourses,
      extraCoursesDiscounts,
      discountsUnit ?? '',
    )

    if (!discount) return 'Добавить курс'

    const nextDiscount = discount?.discount ?? 0
    const stringDiscount =
      discountsUnit === 'percent'
        ? `${nextDiscount}%`
        : `${nextDiscount} рублей`
    return `Добавить курс со скидкой ${stringDiscount}`
  }

  return (
    <div className={cn.wrapper}>
      {!!addedCourses?.length && (
        <div className={cn.courseList}>
          <div className={cn.title}>Дополнительные курсы</div>
          {addedCourses?.map((order, index) => {
            const discount = getDiscount(
              isFirstFree,
              index,
              addedCourses,
              extraCoursesDiscounts,
              discountsUnit ?? '',
            )

            return (
              <SelectCourseCard
                key={index}
                isLoading={isLoading}
                order={order}
                courses={courses?.filter(
                  (item) =>
                    !addedCourses?.some(
                      (selected) =>
                        selected?.course?.id === item?.id &&
                        order?.course?.id !== item?.id,
                    ),
                )}
                promoCode={discount ?? undefined}
                onChange={(key, value) => onChange(index, key, value)}
                {...(canManage && {
                  onDismiss: () => onRemove(index),
                })}
              />
            )
          })}
        </div>
      )}
      {canAddCourse && (
        <SecondaryButton onClick={onClickAdd} className={cn.addCourseButton}>
          {getAddButtonString()}
        </SecondaryButton>
      )}
    </div>
  )
}

export default SelectAdditionalCourses
